/**
 * Public roadmap (#17 of the 19-item backlog).
 *
 * Renders the curated roadmap_public collection — Owner edits via the
 * Strategic Ops page, customers and prospects see it here without auth.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { CheckCircle2, Loader2, Compass, FlaskConical, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  shipped:     { label: "Shipped",      icon: CheckCircle2, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  in_progress: { label: "In progress",  icon: Loader2,      color: "bg-violet-100 text-violet-800 border-violet-200" },
  planned:     { label: "Planned",      icon: Clock,        color: "bg-amber-100 text-amber-800 border-amber-200" },
  exploring:   { label: "Exploring",    icon: FlaskConical, color: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Roadmap() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/public/roadmap`)
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
  }, []);

  const groups = (items || []).reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  return (
    <>
      <Seo title="Roadmap · ERIO" />
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-12" data-testid="public-roadmap-page">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-ek-purple font-semibold">
            EKSELENS · ERIO · ROADMAP
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink sm:text-4xl">
            Where ERIO is heading
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-ek-ink/70 sm:text-base">
            What we've shipped, what's in flight, and what's next.
            Updated whenever a release lands. Want something on the list?
            Email <a href="mailto:info@ekselens.com" className="text-ek-purple underline">info@ekselens.com</a> — every prospect comment shapes the next quarter.
          </p>
        </header>

        {!items && (
          <div className="flex items-center gap-2 text-ek-ink/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {items && items.length === 0 && (
          <p className="text-ek-ink/60">Roadmap coming soon.</p>
        )}

        {items && items.length > 0 && (
          <div className="space-y-10">
            {["shipped", "in_progress", "planned", "exploring"].map((cat) => {
              const meta = CATEGORY_META[cat];
              const group = groups[cat] || [];
              if (group.length === 0) return null;
              const Icon = meta.icon;
              return (
                <section key={cat} data-testid={`roadmap-group-${cat}`}>
                  <div className="mb-4 flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${cat === "in_progress" ? "animate-spin text-violet-500" : "text-ek-ink/60"}`} />
                    <h2 className="font-display text-xl font-semibold text-ek-ink">
                      {meta.label}
                    </h2>
                    <span className="ml-2 text-xs text-ek-ink/45">
                      {group.length} item{group.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="space-y-3">
                    {group.map((it) => (
                      <li
                        key={it.id}
                        data-testid={`roadmap-item-${it.id}`}
                        className={`rounded-2xl border bg-white p-5 ${meta.color.replace(/ text-[a-z]+-\d+/, "").replace(/bg-[a-z]+-\d+/, "")} border-black/5 shadow-sm`}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className="font-semibold text-ek-ink">{it.title}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold border ${meta.color}`}>
                            {it.quarter}
                          </span>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-ek-ink/70">
                          {it.blurb}
                        </p>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <footer className="mt-12 border-t border-black/10 pt-6 text-center text-xs text-ek-ink/55">
          <Compass className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
          Last refreshed live from the platform. Suggest something at{" "}
          <a href="mailto:info@ekselens.com" className="text-ek-purple underline">
            info@ekselens.com
          </a>.
        </footer>
      </main>
    </>
  );
}
